import { useCallback, useMemo, useState } from "react";
import { useWorkspacesList } from "@api/workspace.api";
import type { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { TemplateDrawerTrail } from "@components/settings/ChartOfAccountsTemplates/TemplateDrawerTrail";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useScope } from "@state/scopeContext.lib";
import { ApplyCoaTemplateWizardBody } from "./ApplyCoaTemplateWizardBody";
import styles from "./ApplyCoaTemplateDrawer.module.scss";

interface Props {
    open: boolean;
    onClose: () => void;
    template?: ChartOfAccountsTemplate;
    onAddEntity: () => void;
    onEditTemplate: () => void;
}

export const ApplyCoaTemplateDrawer = ({
    open,
    onClose,
    template,
    onAddEntity,
    onEditTemplate,
}: Props) => {
    const { organizationId } = useScope();
    const { data: workspaces } = useWorkspacesList({
        organizationId: organizationId ?? undefined,
        includeEntities: true,
    });

    const workspaceDataKey = useMemo(
        () =>
            workspaces
                ? workspaces
                      .map((ws) =>
                          [ws.id, ...(ws.entities ?? []).map((e) => e.id)].join(
                              ":",
                          ),
                      )
                      .join("|")
                : "pending",
        [workspaces],
    );

    const remountKey = `${template?.id ?? "none"}::${workspaceDataKey}`;

    const [step, setStep] = useState<1 | 2>(1);

    const handleDrawerClose = useCallback(() => {
        setStep(1);
        onClose();
    }, [onClose]);

    const header = (
        <TemplateDrawerTrail
            title="Apply template"
            templateName={template?.name}
        />
    );

    return (
        <Drawer
            open={open}
            onClose={handleDrawerClose}
            size="fullScreen"
            dismissible={false}
            noPadding
            headerAddonLeft={header}
        >
            {open ? (
                <div className={styles.drawerContent}>
                    <ApplyCoaTemplateWizardBody
                        key={remountKey}
                        template={template}
                        workspaces={workspaces}
                        step={step}
                        setStep={setStep}
                        onClose={handleDrawerClose}
                        onAddEntity={onAddEntity}
                        onEditTemplate={onEditTemplate}
                    />
                </div>
            ) : null}
        </Drawer>
    );
};
